import React from 'react';
import { Button } from '@/components/ui/Button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Pagination } from '@/components/ui/Pagination';
import { EmptyState } from '@/components/ui/EmptyState';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/DropdownMenu';
import { ChevronDown, CheckCircle, XCircle, UserPlus, Building2 } from 'lucide-react';
import { getAuthTypeBadge, getUserStatusBadge } from '@/utils/badgeUtils';
import { formatDateTimeToKST } from '@/utils/dateUtils';
import { User } from '@/types';

interface PendingUsersTableProps {
  users: User[];
  page: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  showCustomerFilter: boolean;
  showCreateCustomer: boolean;
  approvingUserId?: number | null;
  onEditUser: (user: User) => void;
  onApproveUser: (userId: number) => Promise<void>;
  onRejectUser: (user: User) => void;
  onCancelInvite: (user: User) => void;
  onAssignCustomer: (user: User) => void;
  onCreateCustomer: (user: User) => void;
  onShowApproveDialog: (user: User) => void;
  fetchPendingUsers: () => Promise<void>;
  searchQuery: string;
}

export const PendingUsersTable: React.FC<PendingUsersTableProps> = ({
  users,
  page,
  total,
  pageSize,
  onPageChange,
  showCustomerFilter,
  showCreateCustomer,
  approvingUserId,
  onEditUser,
  onApproveUser,
  onRejectUser,
  onCancelInvite,
  onAssignCustomer,
  onCreateCustomer,
  onShowApproveDialog,
  fetchPendingUsers,
  searchQuery,
}) => {
  return (
    <>
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              {/* 슈퍼관리자에게만 "ID" 및 "조직" 컬럼 표시 */}
              {showCustomerFilter && <TableHead>ID</TableHead>}
              {showCustomerFilter && <TableHead>조직</TableHead>}
              <TableHead>상태</TableHead>
              <TableHead>이메일</TableHead>
              <TableHead>이름</TableHead>
              <TableHead>회사</TableHead>
              <TableHead>로그인 유형</TableHead>
              <TableHead>요청일</TableHead>
              <TableHead className="text-right">작업</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={showCustomerFilter ? 9 : 7} className="text-center py-8">
                  <EmptyState
                    message={searchQuery ? '검색 결과가 없습니다.' : '대기 중인 사용자가 없습니다.'}
                  />
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => {
                const isInvited = user.status === 'invited';
                return (
                  <TableRow key={user.id}>
                    {/* 슈퍼관리자에게만 "ID" 및 "조직" 컬럼 표시 */}
                    {showCustomerFilter && (
                      <TableCell className="text-sm text-gray-600">
                        {user.id}
                      </TableCell>
                    )}
                    {showCustomerFilter && (
                      <TableCell className="text-sm">
                        {user.customer?.customer_name || '(미할당)'}
                      </TableCell>
                    )}
                    <TableCell>
                      {getUserStatusBadge(user.status)}
                    </TableCell>
                    <TableCell className="text-sm font-medium">
                      {user.email}
                    </TableCell>
                    <TableCell className="text-sm">
                      {user.name || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {/* 회사명은 User 모델에 저장되지 않으므로 일단 "-"로 표시 */}
                      {/* TODO: 회원가입 시 입력한 companyName을 저장하도록 수정 필요 */}
                      -
                    </TableCell>
                    <TableCell>
                      {getAuthTypeBadge(user.auth_type)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDateTimeToKST(user.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onEditUser(user)}
                        >
                          상세
                        </Button>
                        {/* 승인 대기(pending_approval) */}
                        {!isInvited && (
                          <>
                            {showCreateCustomer ? (
                              <>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="flex flex-col items-center justify-center h-auto py-1.5 px-4 pr-6 relative"
                                    >
                                      <span className="leading-tight">고객사</span>
                                      <span className="leading-tight text-xs">할당/생성</span>
                                      <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => onAssignCustomer(user)}
                                      className="text-blue-600 focus:text-blue-700"
                                    >
                                      <UserPlus className="mr-2 h-4 w-4" />
                                      고객사 할당
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => onCreateCustomer(user)}
                                      className="text-green-600 focus:text-green-700"
                                    >
                                      <Building2 className="mr-2 h-4 w-4" />
                                      고객사 생성
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      disabled={approvingUserId === user.id}
                                      className={approvingUserId === user.id ? '' : 'flex flex-col items-center justify-center h-auto py-1.5 px-4 pr-6 relative'}
                                    >
                                      {approvingUserId === user.id ? (
                                        '처리 중...'
                                      ) : (
                                        <>
                                          <span className="leading-tight">승인</span>
                                          <span className="leading-tight text-xs">거부</span>
                                          <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3" />
                                        </>
                                      )}
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-24 min-w-0">
                                    <DropdownMenuItem
                                      onClick={async () => {
                                        // 고객사가 없으면 다이얼로그 띄우기
                                        if (!user.customer) {
                                          onShowApproveDialog(user);
                                        } else {
                                          // 고객사가 있으면 바로 승인
                                          try {
                                            await onApproveUser(user.id);
                                            await fetchPendingUsers();
                                          } catch (error) {
                                            // 에러는 상위 컴포넌트에서 처리
                                          }
                                        }
                                      }}
                                      className="text-green-600 focus:text-green-700"
                                    >
                                      <CheckCircle className="mr-2 h-4 w-4" />
                                      승인
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => onRejectUser(user)}
                                      className="text-red-600 focus:text-red-700"
                                    >
                                      <XCircle className="mr-2 h-4 w-4" />
                                      거부
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={approvingUserId === user.id}
                                    className={approvingUserId === user.id ? '' : 'flex flex-col items-center justify-center h-auto py-1.5 px-4 pr-6 relative'}
                                  >
                                    {approvingUserId === user.id ? (
                                      '처리 중...'
                                    ) : (
                                      <>
                                        <span className="leading-tight">승인</span>
                                        <span className="leading-tight text-xs">거부</span>
                                        <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 h-3 w-3" />
                                      </>
                                    )}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-24 min-w-0">
                                  <DropdownMenuItem
                                    onClick={async () => {
                                      try {
                                        await onApproveUser(user.id);
                                        await fetchPendingUsers();
                                      } catch {
                                        // 상위에서 에러 처리
                                      }
                                    }}
                                    className="text-green-600 focus:text-green-700"
                                  >
                                    <CheckCircle className="mr-2 h-4 w-4" />
                                    승인
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => onRejectUser(user)}
                                    className="text-red-600 focus:text-red-700"
                                  >
                                    <XCircle className="mr-2 h-4 w-4" />
                                    거부
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </>
                        )}
                        {/* 초대 대기(invited): 취소만 표시 */}
                        {isInvited && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => onCancelInvite(user)}
                          >
                            취소
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      
      <Pagination
        currentPage={page}
        totalPages={Math.ceil(total / pageSize)}
        totalItems={total}
        itemsPerPage={pageSize}
        onPageChange={onPageChange}
        showItemsPerPage={false}
        showTotal={true}
        className="mt-4"
      />
    </>
  );
};

